/**
 * Covermatic Studio runtime primitives and shared contracts.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;

  /**
   * @typedef {Object} AlphaGuestImage
   * @property {string} URL
   * @property {string} ['Artist Name']
   * @property {string} ['Song/Album Name']
   * @property {string} Genre
   * @property {string} ['Visual Concept']
   */

  /**
   * @typedef {Object} AlphaPremadeAsset
   * @property {string} id
   * @property {string} title
   * @property {string} imageUrl
   * @property {string[]} keywords
   */

  /**
   * @typedef {Object} AlphaSignatureStyle
   * @property {string} id
   * @property {string} name
   * @property {string} description
   * @property {string[]} tags
   * @property {string} imageUrl
   * @property {string} primaryImageUrl
   * @property {string} dataSource
   * @property {string} feedUrl
   * @property {string} ctaLabel
   * @property {string} detailCtaLabel
   * @property {string} detailDescription
   * @property {AlphaPremadeAsset[]} assets
   * @property {string} systemPrompt
   */

  /**
   * @typedef {'premade'|'style'} AlphaSelectionType
   */

  /**
   * @typedef {'premade'|'style-asset'|'style-custom'} AlphaSelectionMode
   */

  /**
   * @typedef {Object} AlphaTemplateSelection
   * @property {string|null} id
   * @property {string} title
   * @property {string} imageUrl
   * @property {string|null} base64
   * @property {string|null} handle
   * @property {AlphaSelectionType} selectionType
   * @property {AlphaSelectionMode} selectionMode
   * @property {string|null} styleId
   * @property {number|null} styleAssetIndex
   * @property {string|null} styleAssetId
   * @property {string|null} styleAssetTitle
   * @property {string|null} systemPrompt
   */

  /**
   * @typedef {Object} AlphaRuntimeConfig
   * @property {string|null} customerId
   * @property {string} apiBase
   * @property {number} perFetch
   * @property {number} creditsGen
   * @property {number} creditsEdit
   * @property {string} returnTo
   * @property {string} sectionId
   * @property {number} credits
   * @property {string} templateSearchApi
   * @property {number} maxUploadFiles
   * @property {number} maxUploadFileSizeMb
   * @property {Record<string, string>} packs
   * @property {AlphaGuestImage[]} guestImages
   * @property {AlphaPremadeAsset[]} prototypePremades
   * @property {AlphaSignatureStyle[]} signatureStyles
   */

  const DEFAULT_PREMADE_CONCEPT = 'Keep the artwork and font style the same';
  const DEFAULT_TEMPLATE_SEARCH_API = window.location.origin + '/apps/covermatic/signature-search';
  const DEFAULT_MODAL_IDS = ['studio-modal', 'tpl-modal', 'template-preview-modal', 'modal', 'buy-modal', 'promo-graphics-modal', 'creative-lab-assets-modal'];

  const Selectors = Object.freeze({
    buyModal: '#buy-modal',
    buyModalCheckout: '#btn-checkout',
    buyModalSignInLink: '#buy-modal-signin-link',
    clearTasks: '#clear-tasks',
    clearTasksModal: '#clear-tasks-modal',
    concept: '#concept',
    creativeLabAssetsCount: '#creative-lab-assets-count',
    creativeLabAssetsDone: '#creative-lab-assets-done',
    creativeLabAssetsEmpty: '#creative-lab-assets-empty',
    creativeLabAssetsGrid: '#creative-lab-assets-grid',
    creativeLabAssetsHelper: '#creative-lab-assets-helper',
    creativeLabAssetsLoadMore: '#creative-lab-assets-load-more',
    creativeLabAssetsLoading: '#creative-lab-assets-loading',
    creativeLabAssetsModal: '#creative-lab-assets-modal',
    creativeLabBrief: '#creative-lab-brief',
    creativeLabClearAssets: '#creative-lab-clear-assets',
    creativeLabError: '#creative-lab-error',
    creativeLabGenerate: '#creative-lab-generate',
    creativeLabGoHome: '#creative-lab-go-home',
    creativeLabLoading: '#creative-lab-loading',
    creativeLabOpenAssets: '#creative-lab-open-assets',
    creativeLabPanel: '#creative-lab-panel',
    creativeLabSelectedAssets: '#creative-lab-selected-assets',
    creativeLabSelectedAssetsGrid: '#creative-lab-selected-assets-grid',
    creativeLabSize: '#creative-lab-size',
    creativeLabType: '#creative-lab-type',
    creativeLabUploadGrid: '#creative-lab-upload-grid',
    creativeLabUploadInput: '#creative-lab-upload-input',
    creativeLabUploadPreview: '#creative-lab-upload-preview',
    creativeLabUploadZone: '#creative-lab-upload-zone',
    creditsValue: '#cm-credits',
    editError: '#edit-error',
    editLoading: '#edit-loading',
    editPanel: '#edit-panel',
    editPanelAnchor: '#edit-panel-anchor',
    editOpenButton: '#btn-edit-open',
    editPhoto: '#edit-photo',
    editCloseMobileButton: '#btn-edit-close-mobile',
    editOverlayMobile: '#edit-overlay-mobile',
    editOverlayMobileSlot: '#edit-overlay-mobile-slot',
    editReferenceClose: '#edit-reference-close',
    editReferenceLarge: '#edit-reference-large',
    editReferenceLightbox: '#edit-reference-lightbox',
    downloadButton: '#btn-download',
    shareButton: '#btn-share-artwork',
    editReferenceOpen: '#edit-reference-open',
    editReferenceThumb: '#edit-reference-thumb',
    editReferenceWrap: '#edit-reference-wrap',
    editSelectedTemplate: '#edit-selected-template',
    editTemplateThumb: '#edit-template-thumb',
    editTemplateTitle: '#edit-template-title',
    editText: '#edit-text',
    editUploadPreview: '#edit-upload-preview',
    editUploadZone: '#edit-upload-zone',
    fetchEnd: '#fetch-end',
    fetchLoading: '#fetch-loading',
    fetchSentinel: '#fetch-sentinel',
    formPanel: '#cm-form-panel',
    galleryGrid: '#grid',
    galleryScrollContainer: '#gallery-scroll-container',
    generateButton: '#btn-generate',
    generateError: '#gen-error',
    generateLoading: '#gen-loading',
    guestExamplesContainer: '#guest-examples-container',
    guestPremadeContainer: '#guest-premade-container',
    imageModal: '#modal',
    imageModalGuest: '#modal-guest',
    imageModalLoggedIn: '#modal-logged-in',
    imageModalNav: '#modal-image-nav',
    lineageNextButton: '#btn-lineage-next',
    lineagePrevButton: '#btn-lineage-prev',
    lineageStatus: '#modal-lineage-status',
    modalImage: '#modal-img',
    modalImageLoadMore: '#btn-image-load-more',
    modalImageNext: '#btn-image-next',
    modalImagePrev: '#btn-image-prev',
    modalNextLoading: '#modal-next-loading',
    openBuyTrigger: '#open-buy, [data-open-buy]',
    packButtons: '.pack',
    packGrid: '#pack-grid',
    promoGraphicsModal: '#promo-graphics-modal',
    promoGraphicsOpenButton: '#btn-promo-graphics-open',
    removeTemplate: '#remove-template',
    runtimeConfig: '#alpha-runtime-config',
    selectedTemplate: '#selected-template',
    startingPoint: '#cm-starting-point',
    studioDescription: '#studio-description',
    studioModal: '#studio-modal',
    studioTitle: '#studio-title',
    studioSignatureStyleDetailAssets: '#studio-style-detail-assets',
    studioSignatureStyleDetailCreate: '#studio-style-detail-create',
    studioSignatureStyleDetailCopy: '#studio-style-detail-copy',
    studioSignatureStyleDetailTitle: '#studio-style-detail-title',
    studioSignatureStyleDetailView: '#studio-signature-style-detail-view',
    studioSignatureStyleGrid: '#studio-signature-style-grid',
    taskCount: '#task-count',
    taskCountModal: '#task-count-modal',
    taskList: '#task-list',
    taskListModal: '#task-list-modal',
    taskManager: '#task-manager',
    taskManagerModal: '#task-manager-modal',
    templateAjaxContainer: '#TemplateAjaxContainer',
    templateBackToStudio: '#tpl-back-to-studio',
    templateClearSearch: '#clear-template-search',
    templateDefaultView: '#template-default-view',
    templateGrid: '#TemplateGridContainer',
    templateInfiniteScrollEnd: '#template-infinite-scroll-end',
    templateInfiniteScrollLoading: '#template-infinite-scroll-loading',
    templateInfiniteScrollSentinel: '#template-infinite-scroll-sentinel',
    templateNextUrl: '#template-next-url',
    templateNoResults: '#template-no-results',
    templateSearch: '#template-search',
    templateSearchGrid: '#template-search-grid',
    templateSearchLoading: '#template-search-loading',
    templateSearchResults: '#template-search-results',
    templateThumb: '#template-thumb',
    templateTitle: '#template-title',
    title: '#title',
    tplModal: '#tpl-modal',
    uploadPreview: '#upload-preview',
    uploadZone: '#upload-zone',
  });

  const StorageKeys = {
    autosave: 'cm_autosave',
    tasks: 'cm_tasks',
  };

  const eventTarget = new EventTarget();
  const Events = Object.freeze({
    emit(name, detail = {}) {
      eventTarget.dispatchEvent(new CustomEvent(name, { detail }));
    },
    on(name, handler, options) {
      const wrapped = (event) => handler(event);
      eventTarget.addEventListener(name, wrapped, options);
      return () => eventTarget.removeEventListener(name, wrapped, options);
    },
  });

  const Services = Object.create(null);
  const registerService = (name, service) => {
    Services[name] = service;
    return service;
  };
  const getService = (name) => Services[name] || null;

  const resolveSelector = (selectorOrKey) => Selectors[selectorOrKey] || selectorOrKey;

  const parseRuntimeConfig = () => {
    const runtimeConfigEl = document.querySelector(resolveSelector('runtimeConfig'));
    if (!runtimeConfigEl) {
      console.error('Covermatic runtime config element was not found');
      return {};
    }

    try {
      return JSON.parse(runtimeConfigEl.textContent || '{}');
    } catch (error) {
      console.error('Invalid Covermatic runtime config', error);
      return {};
    }
  };

  const normalizeString = (value, fallback = '') => {
    if (typeof value === 'string' && value.trim()) return value.trim();
    return fallback;
  };

  const normalizeMediaUrl = (value) => {
    const normalized = normalizeString(value, '');
    if (!normalized) return '';
    if (normalized.startsWith('//')) return `https:${normalized}`;
    return normalized;
  };

  const normalizeNumber = (value, fallback) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  const normalizeIdentifier = (value) => {
    if (value == null) return null;
    const normalized = String(value).trim();
    return normalized && normalized !== 'null' ? normalized : null;
  };

  const normalizeArray = (value) => (Array.isArray(value) ? value : []);

  const normalizeSelectionType = (value, fallback = 'premade') => (
    value === 'style' ? 'style' : fallback
  );

  const normalizeSelectionMode = (value, selectionType) => {
    if (selectionType === 'style') {
      return value === 'style-asset' ? 'style-asset' : 'style-custom';
    }
    return 'premade';
  };

  const normalizePackMap = (value) => {
    if (!value || typeof value !== 'object') return {};

    return Object.entries(value).reduce((packs, [key, packValue]) => {
      const normalizedKey = String(key).trim();
      const normalizedValue = packValue == null ? '' : String(packValue).trim();
      if (normalizedKey && normalizedValue) packs[normalizedKey] = normalizedValue;
      return packs;
    }, {});
  };

  /**
   * @param {unknown} value
   * @returns {AlphaTemplateSelection|null}
   */
  const normalizeTemplateBehavior = (value, selectionType, selectionMode) => {
    const normalized = normalizeString(value, '');
    if (normalized === 'template-image' || normalized === 'style-custom') return normalized;
    if (selectionType === 'premade') return 'template-image';
    return selectionMode === 'style-asset' ? 'template-image' : 'style-custom';
  };

  const normalizeTemplateSelection = (value) => {
    if (!value || typeof value !== 'object') return null;

    const imageUrl = normalizeMediaUrl(value.imageUrl || value.image);
    if (!imageUrl) return null;

    const selectionType = normalizeSelectionType(value.selectionType);
    const selectionMode = normalizeSelectionMode(value.selectionMode, selectionType);
    const styleAssetIndex = Number(value.styleAssetIndex);
    const templateBehavior = normalizeTemplateBehavior(value.templateBehavior, selectionType, selectionMode);

    return {
      id: normalizeString(value.id, '') || null,
      title: normalizeString(value.title, 'Selected direction'),
      imageUrl,
      base64: normalizeString(value.base64, '') || null,
      handle: normalizeString(value.handle, '') || null,
      selectionType,
      selectionMode,
      templateBehavior,
      styleId: normalizeString(value.styleId, '') || null,
      styleAssetIndex: Number.isFinite(styleAssetIndex) && styleAssetIndex >= 0 ? styleAssetIndex : null,
      styleAssetId: normalizeString(value.styleAssetId, '') || null,
      styleAssetTitle: normalizeString(value.styleAssetTitle, '') || null,
      systemPrompt: normalizeString(value.systemPrompt, '') || null,
    };
  };

  const normalizeGuestImages = (images) => normalizeArray(images).reduce((list, item) => {
    const url = normalizeString(item?.URL);
    if (!url) return list;

    list.push({
      URL: url,
      'Artist Name': normalizeString(item?.['Artist Name']),
      'Song/Album Name': normalizeString(item?.['Song/Album Name']),
      Genre: normalizeString(item?.Genre),
      'Visual Concept': normalizeString(item?.['Visual Concept']),
    });
    return list;
  }, []);

  const normalizePremades = (premades) => normalizeArray(premades).reduce((list, item, index) => {
    const imageUrl = normalizeString(item?.imageUrl);
    if (!imageUrl) return list;

    list.push({
      id: normalizeString(item?.id, `premade-${index + 1}`),
      title: normalizeString(item?.title, `Premade ${index + 1}`),
      imageUrl,
      keywords: normalizeArray(item?.keywords).map((keyword) => normalizeString(keyword)).filter(Boolean),
    });
    return list;
  }, []);

  const normalizeSignatureStyles = (styles, premades) => {
    const premadeMap = premades.reduce((map, asset) => {
      map.set(asset.id, asset);
      return map;
    }, new Map());

    return normalizeArray(styles).reduce((list, item, index) => {
      const id = normalizeString(item?.id, `style-${index + 1}`);
      const name = normalizeString(item?.name, id);
      const imageUrl = normalizeString(item?.imageUrl, premades[0]?.imageUrl || '');
      const primaryImageUrl = normalizeString(item?.primaryImageUrl, imageUrl);
      const assetIds = normalizeArray(item?.assetIds).map((assetId) => normalizeString(assetId)).filter(Boolean);
      const assetsFromIds = assetIds.map((assetId) => premadeMap.get(assetId)).filter(Boolean);
      const assets = assetsFromIds.length ? assetsFromIds : premades;

      list.push({
        id,
        name,
        description: normalizeString(item?.description),
        tags: normalizeArray(item?.tags).map((tag) => normalizeString(tag)).filter(Boolean),
        imageUrl,
        primaryImageUrl,
        dataSource: normalizeString(item?.dataSource),
        feedUrl: normalizeString(item?.feedUrl),
        ctaLabel: normalizeString(item?.ctaLabel, 'Explore Style'),
        detailCtaLabel: normalizeString(item?.detailCtaLabel, `Use ${name}`),
        detailDescription: normalizeString(item?.detailDescription),
        assets,
        systemPrompt: normalizeString(item?.systemPrompt),
      });
      return list;
    }, []);
  };

  const normalizeApiBase = (value) => {
    const rawApiBase = normalizeString(value, '/apps/covermatic/');
    const normalizedApiBase = rawApiBase.replace(/\/+$/, '');

    if (/^(\/covermatic|\/apps\/covermatic(?:\/app_proxy)?)$/i.test(normalizedApiBase)) {
      return '/apps/covermatic/';
    }

    if (/^(\/cag|\/apps\/cag(?:\/app_proxy)?)$/i.test(normalizedApiBase)) {
      return '/apps/covermatic/';
    }

    return `${normalizedApiBase}/`;
  };

  const validateRuntimeConfig = (value) => {
    const errors = [];
    const warnings = [];

    if (!value || typeof value !== 'object') {
      errors.push('Runtime config must be a JSON object.');
      return { isValid: false, errors, warnings };
    }

    if (!normalizeString(value.apiBase)) warnings.push('Missing apiBase. Falling back to /apps/covermatic/.');
    if (!normalizePackMap(value.packs) || Object.keys(normalizePackMap(value.packs)).length === 0) {
      errors.push('Runtime config packs are missing.');
    }
    if (!normalizeArray(value.guestImages).length) warnings.push('guestImages is empty.');
    if (!normalizeArray(value.prototypePremades).length) warnings.push('prototypePremades is empty.');
    if (!normalizeArray(value.signatureStyles).length) warnings.push('signatureStyles is empty.');

    return { isValid: errors.length === 0, errors, warnings };
  };

  /**
   * @param {AlphaTemplateSelection|Record<string, unknown>|null|undefined} template
   * @returns {boolean}
   */
  const shouldAppendTemplatePrompt = (template) => {
    if (!template || typeof template !== 'object') return false;
    return shouldSendTemplateImageInput(template);
  };

  /**
   * @param {AlphaTemplateSelection|Record<string, unknown>|null|undefined} template
   * @returns {boolean}
   */
  const shouldSendTemplateImageInput = (template) => {
    if (!template || typeof template !== 'object') return false;

    const normalizedTemplate = normalizeTemplateSelection(template);
    if (!normalizedTemplate) return false;

    return normalizedTemplate.templateBehavior === 'template-image';
  };

  /**
   * @param {string|null|undefined} concept
   * @param {AlphaTemplateSelection|Record<string, unknown>|null|undefined} template
   * @param {string} [promptSuffix]
   * @returns {string}
   */
  const buildGenerationConcept = (concept, template, promptSuffix = DEFAULT_PREMADE_CONCEPT) => {
    const userConcept = normalizeString(concept);
    if (!shouldAppendTemplatePrompt(template)) return userConcept;
    return userConcept ? `${userConcept}\n\n${promptSuffix}` : promptSuffix;
  };

  /**
   * @param {Record<string, unknown>} value
   * @returns {AlphaRuntimeConfig & { maxUploadFileSizeBytes: number, guestImageMap: Record<string, AlphaGuestImage> }}
   */
  const normalizeRuntimeConfig = (value) => {
    const prototypePremades = normalizePremades(value?.prototypePremades);
    const signatureStyles = normalizeSignatureStyles(value?.signatureStyles, prototypePremades);
    const guestImages = normalizeGuestImages(value?.guestImages);

    return {
      customerId: normalizeIdentifier(value?.customerId),
      apiBase: normalizeApiBase(value?.apiBase),
      toolSlug: normalizeString(value?.toolSlug, 'covermatic'),
      nicheSlug: normalizeString(value?.nicheSlug, 'cover_art'),
      assetFamily: normalizeString(value?.assetFamily, 'cover_art'),
      assetType: normalizeString(value?.assetType, 'cover_art'),
      sizePreset: normalizeString(value?.sizePreset, 'square_1_1'),
      imageAspectRatio: normalizeString(value?.imageAspectRatio, '1:1'),
      perFetch: normalizeNumber(value?.perFetch, 20),
      creditsGen: normalizeNumber(value?.creditsGen, 5),
      creditsEdit: normalizeNumber(value?.creditsEdit, 5),
      returnTo: normalizeString(value?.returnTo, '/pages/covermatic-ai-studio'),
      sectionId: normalizeString(value?.sectionId, 'covermatic'),
      credits: normalizeNumber(value?.credits, 0),
      templateSearchApi: normalizeString(value?.templateSearchApi, DEFAULT_TEMPLATE_SEARCH_API),
      maxUploadFiles: normalizeNumber(value?.maxUploadFiles, 10),
      maxUploadFileSizeMb: normalizeNumber(value?.maxUploadFileSizeMb, 20),
      maxUploadFileSizeBytes: normalizeNumber(value?.maxUploadFileSizeMb, 20) * 1024 * 1024,
      packs: normalizePackMap(value?.packs),
      guestImages,
      guestImageMap: guestImages.reduce((map, item) => {
        map[item.URL] = item;
        return map;
      }, {}),
      prototypePremades,
      signatureStyles,
    };
  };

  /** @type {AlphaRuntimeConfig} */
  const runtimeConfig = parseRuntimeConfig();
  const validation = validateRuntimeConfig(runtimeConfig);
  validation.warnings.forEach((message) => console.warn(`[CovermaticRuntime] ${message}`));
  if (!validation.isValid) {
    validation.errors.forEach((message) => console.error(`[CovermaticRuntime] ${message}`));
  }

  const normalizedRuntimeConfig = normalizeRuntimeConfig(runtimeConfig);
  const storageNamespace = `${normalizedRuntimeConfig.toolSlug}:${normalizedRuntimeConfig.nicheSlug}`
    .replace(/[^a-z0-9:_-]+/gi, '_')
    .toLowerCase();
  if (normalizedRuntimeConfig.toolSlug !== 'covermatic' || normalizedRuntimeConfig.nicheSlug !== 'cover_art') {
    StorageKeys.autosave = `cm_autosave:${storageNamespace}`;
    StorageKeys.tasks = `cm_tasks:${storageNamespace}`;
  }

  const Config = Object.freeze({
    customerId: normalizedRuntimeConfig.customerId,
    apiBase: normalizedRuntimeConfig.apiBase,
    toolSlug: normalizedRuntimeConfig.toolSlug,
    nicheSlug: normalizedRuntimeConfig.nicheSlug,
    assetFamily: normalizedRuntimeConfig.assetFamily,
    assetType: normalizedRuntimeConfig.assetType,
    storageNamespace,
    perFetch: normalizedRuntimeConfig.perFetch,
    creditsGen: normalizedRuntimeConfig.creditsGen,
    creditsEdit: normalizedRuntimeConfig.creditsEdit,
    returnTo: normalizedRuntimeConfig.returnTo,
    sectionId: normalizedRuntimeConfig.sectionId,
    templateSearchApi: normalizedRuntimeConfig.templateSearchApi,
    maxUploadFiles: normalizedRuntimeConfig.maxUploadFiles,
    maxUploadFileSizeBytes: normalizedRuntimeConfig.maxUploadFileSizeBytes,
    packs: normalizedRuntimeConfig.packs,
    guestImages: normalizedRuntimeConfig.guestImages,
    guestImageMap: normalizedRuntimeConfig.guestImageMap,
    prototypePremades: normalizedRuntimeConfig.prototypePremades,
    signatureStyles: normalizedRuntimeConfig.signatureStyles,
  });

  const createReactiveState = (initialState) => new Proxy({ ...initialState }, {
    set(target, prop, value) {
      const previous = target[prop];
      target[prop] = value;

      if (previous !== value) {
        const detail = { key: prop, value, previous, state: target };
        Events.emit('state:change', detail);
        Events.emit(`state:${String(prop)}`, detail);
      }

      return true;
    },
  });

  const State = createReactiveState({
    credits: normalizedRuntimeConfig.credits,
    activeUploads: 0,
    page: 0,
    hasMore: true,
    isFetching: false,
    urlSet: new Set(),
      currentUrl: null,
      currentImageRecord: null,
      template: null,
      entryChoice: null,
      creativeLabAssets: [],
      pendingPromoGraphicAsset: null,
      editTemplate: null,
      editMode: false,
  });

  const Utils = {
    resolveSelector,
    $: (selectorOrKey, root = document) => root.querySelector(resolveSelector(selectorOrKey)),
    $$: (selectorOrKey, root = document) => [...root.querySelectorAll(resolveSelector(selectorOrKey))],
    escapeHtml: (value) => {
      if (value == null) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    },
    toast: (message, type = 'info') => {
      const el = document.createElement('div');
      const color = type === 'error' ? 'bg-red-600' : type === 'warn' ? 'bg-amber-500' : 'bg-gray-900';
      el.className = `${color} text-white text-sm px-3 py-2 rounded-full shadow-lg fixed left-1/2 -translate-x-1/2 bottom-5 z-[60]`;
      el.textContent = message;
      document.body.appendChild(el);
      window.setTimeout(() => el.remove(), 2500);
    },
    isLoggedIn: () => Config.customerId !== null,
    debounce: (func, wait) => {
      let timeout;
      return function debounced(...args) {
        clearTimeout(timeout);
        timeout = window.setTimeout(() => func.apply(this, args), wait);
      };
    },
    readStorage: (key, fallback = null) => {
      try {
        const value = window.localStorage.getItem(key);
        return value == null ? fallback : value;
      } catch (error) {
        return fallback;
      }
    },
    writeStorage: (key, value) => {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        console.warn(`[CovermaticRuntime] Failed to persist ${key}`, error);
      }
    },
    removeStorage: (key) => {
      try {
        window.localStorage.removeItem(key);
      } catch (error) {
        console.warn(`[CovermaticRuntime] Failed to clear ${key}`, error);
      }
    },
    withTimeout: async (input, init = {}, timeoutMs = 8000) => {
      const timeout = Number(timeoutMs);
      if (!Number.isFinite(timeout) || timeout <= 0 || typeof AbortController !== 'function') {
        return fetch(input, init);
      }

      const controller = new AbortController();
      const timer = window.setTimeout(() => controller.abort(), timeout);
      const mergedInit = { ...init, signal: controller.signal };

      try {
        return await fetch(input, mergedInit);
      } finally {
        window.clearTimeout(timer);
      }
    },
    syncBodyLock: (modalIds = DEFAULT_MODAL_IDS) => {
      const hasOpenModal = modalIds.some((id) => {
        const element = document.getElementById(id);
        return element && !element.classList.contains('hidden');
      });

      document.body.classList.toggle('overflow-hidden', hasOpenModal);
    },
    initSignatureSlideshows: (root = document) => {
      const shuffleIndexes = (size) => {
        const order = Array.from({ length: size }, (_value, index) => index);
        for (let index = order.length - 1; index > 0; index -= 1) {
          const swapIndex = Math.floor(Math.random() * (index + 1));
          [order[index], order[swapIndex]] = [order[swapIndex], order[index]];
        }
        return order;
      };

      const buildCycleOrder = (size, previousTail = []) => {
        const order = shuffleIndexes(size);
        if (size < 2 || !previousTail.length) return order;

        const protectedCount = Math.min(size - 1, Math.min(previousTail.length, 3));
        const blocked = new Set(previousTail.slice(0, protectedCount));
        let nextStartIndex = order.findIndex((index) => !blocked.has(index));

        if (nextStartIndex === -1) nextStartIndex = order.findIndex((index) => index !== previousTail[0]);
        if (nextStartIndex > 0) [order[0], order[nextStartIndex]] = [order[nextStartIndex], order[0]];

        return order;
      };

      Utils.$$('[data-signature-slideshow]', root).forEach((slideshow) => {
        if (slideshow.dataset.slideshowInitialized === 'true') return;

        const slides = Utils.$$('[data-signature-slide]', slideshow);
        if (!slides.length) return;

        let currentOrder = buildCycleOrder(slides.length);
        let orderIndex = 0;
        let current = currentOrder[orderIndex];
        slides.forEach((slide, index) => {
          slide.classList.toggle('is-active', index === current);
        });
        slideshow.dataset.slideshowInitialized = 'true';

        if (slides.length < 2) return;

        const queueNextTransition = () => {
          const interval = 2400 + Math.floor(Math.random() * 1200);

          window.setTimeout(() => {
            let nextOrderIndex = orderIndex + 1;

            if (nextOrderIndex >= currentOrder.length) {
              const previousTail = currentOrder
                .slice(Math.max(0, currentOrder.length - Math.min(3, currentOrder.length)))
                .reverse();
              currentOrder = buildCycleOrder(slides.length, previousTail);
              nextOrderIndex = 0;
            }

            let next = currentOrder[nextOrderIndex];
            if (next === current) {
              currentOrder = buildCycleOrder(slides.length, [current]);
              nextOrderIndex = 0;
              next = currentOrder[nextOrderIndex];
            }

            slides[current].classList.remove('is-active');
            slides[next].classList.add('is-active');
            current = next;
            orderIndex = nextOrderIndex;
            queueNextTransition();
          }, interval);
        };

        queueNextTransition();
      });
    },
  };

  class ApiClient {
    static buildApiUrl(endpoint, customerId = Config.customerId) {
      const url = new URL(Config.apiBase + endpoint, window.location.origin);
      if (customerId) {
        url.searchParams.set('customerId', customerId);
      }
      return url.toString();
    }

    static async getUploadUrl(fileType) {
      const res = await fetch(ApiClient.buildApiUrl('upload-url'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: Config.customerId, fileType }),
      });
      if (!res.ok) throw new Error('Failed to get signed URL');
      return res.json();
    }

    static async uploadFile(signedUrl, file) {
      const res = await fetch(signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!res.ok) throw new Error('File upload failed');
      return true;
    }

    static async submitJob(endpoint, body) {
      const res = await fetch(ApiClient.buildApiUrl(endpoint, body?.customerId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await res.text().catch(() => '');
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = null;
      }

      if (!res.ok) {
        const message = typeof data?.error === 'string' ? data.error : text;
        throw new Error(ApiClient.normalizeErrorMessage(message, res.status, 'Request failed'));
      }
      return data || {};
    }

    static async checkJob(jobId) {
      return fetch(`${Config.apiBase}check-job?jobId=${jobId}`);
    }

    static appendToolContext(searchParams) {
      if (!searchParams || typeof searchParams.set !== 'function') return searchParams;
      if (Config.toolSlug) searchParams.set('toolSlug', Config.toolSlug);
      if (Config.nicheSlug) searchParams.set('nicheSlug', Config.nicheSlug);
      if (Config.assetFamily) searchParams.set('assetFamily', Config.assetFamily);
      if (Config.assetType) searchParams.set('assetType', Config.assetType);
      return searchParams;
    }

    static async fetchHistoricalGenerations(limit = Config.perFetch, offset = 0) {
      const query = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });
      ApiClient.appendToolContext(query);
      const apiBase = String(Config.apiBase || '/').replace(/\/+$/, '');
      const candidates = [
        `${apiBase}/app_proxy/historical-generations?${query}`,
        `${apiBase}/api/historical-generations?${query}`,
        `${apiBase}/historical-generations?${query}`,
      ];

      let lastStatus = 0;
      for (const url of candidates) {
        const res = await Utils.withTimeout(url, { credentials: 'same-origin' }, 8000);
        if (res.ok) return res.json();
        lastStatus = res.status;
        if (res.status !== 401 && res.status !== 404) {
          throw new Error(`Historical fetch failed with ${res.status}`);
        }
      }

      throw new Error(`Historical fetch failed with ${lastStatus || 'unknown status'}`);
    }

    static async shareArtwork(record) {
      const body = {
        customerId: Config.customerId,
        coverArtId: record?.id ?? null,
        fileName: record?.fileName || record?.uniqueFileName || '',
        uniqueFileName: record?.uniqueFileName || record?.fileName || '',
        imageUrl: record?.url || '',
      };
      const res = await fetch(ApiClient.buildApiUrl('share-artwork', Config.customerId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify(body),
      });
      const text = await res.text().catch(() => '');
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = null;
      }
      if (!res.ok) {
        const message = typeof data?.error === 'string' ? data.error : text;
        throw new Error(ApiClient.normalizeErrorMessage(message, res.status, 'Share failed. Please try again.'));
      }
      return data || {};
    }

    static async fetchCredits() {
      const res = await Utils.withTimeout(
        `${Config.apiBase}get-customer-credits?customerId=${Config.customerId}`,
        {},
        5000,
      );
      if (!res.ok) throw new Error('Credit fetch failed');
      return res.json();
    }

    static async fetchFreeGenerationStatus() {
      const res = await Utils.withTimeout(
        ApiClient.buildApiUrl('free-generation-status', Config.customerId),
        { credentials: 'same-origin' },
        5000,
      );
      if (!res.ok) throw new Error('Free generation status failed');
      return res.json();
    }

    static async replenishFreeGenerations() {
      const res = await fetch(ApiClient.buildApiUrl('replenish-free-generations', Config.customerId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ customerId: Config.customerId }),
      });
      const text = await res.text().catch(() => '');
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch (error) {
        data = null;
      }
      if (!res.ok) {
        const message = typeof data?.error === 'string' ? data.error : text;
        throw new Error(ApiClient.normalizeErrorMessage(message, res.status, 'Could not buy more generations.'));
      }
      return data || {};
    }

    static async buildFriendlyError(response, fallbackMessage) {
      const text = await response.text().catch(() => '');
      const trimmed = String(text || '').trim();

      if (trimmed) {
        try {
          const parsed = JSON.parse(trimmed);
          const message = parsed?.error || parsed?.message;
          if (typeof message === 'string' && message.trim()) {
            return ApiClient.normalizeErrorMessage(message.trim(), response.status, fallbackMessage);
          }
        } catch (error) {
          // Response is not JSON. Fall through to sanitize raw text.
        }
      }

      return ApiClient.normalizeErrorMessage(trimmed, response.status, fallbackMessage);
    }

    static normalizeErrorMessage(message, status, fallbackMessage) {
      const raw = String(message || '').trim();
      const lowered = raw.toLowerCase();
      const looksLikeMarkup =
        lowered.includes('<!doctype') ||
        lowered.includes('<html') ||
        lowered.includes('<body') ||
        lowered.includes('<script') ||
        lowered.includes('window.__') ||
        lowered.includes('monorail') ||
        lowered.includes('your account');

      if (status === 401 || status === 403) return 'Please log in and try again.';
      if (status >= 500) return 'The service is temporarily unavailable. Try again in a minute.';
      if (looksLikeMarkup || raw.length > 220) return fallbackMessage || 'Request failed. Please try again.';
      if (!raw) return fallbackMessage || `Request failed with ${status}`;
      return raw;
    }
  }

  runtime.runtimeConfig = runtimeConfig;
  runtime.validation = validation;
  runtime.Selectors = Selectors;
  runtime.StorageKeys = StorageKeys;
  runtime.Events = Events;
  runtime.Services = Services;
  runtime.registerService = registerService;
  runtime.getService = getService;
  runtime.Config = Config;
  runtime.State = State;
  runtime.Utils = Utils;
  runtime.ApiClient = ApiClient;
  runtime.DEFAULT_PREMADE_CONCEPT = DEFAULT_PREMADE_CONCEPT;
  runtime.validateRuntimeConfig = validateRuntimeConfig;
  runtime.normalizeRuntimeConfig = normalizeRuntimeConfig;
  runtime.normalizeTemplateSelection = normalizeTemplateSelection;
  runtime.shouldAppendTemplatePrompt = shouldAppendTemplatePrompt;
  runtime.shouldSendTemplateImageInput = shouldSendTemplateImageInput;
  runtime.buildGenerationConcept = buildGenerationConcept;
  runtime.normalizeApiBase = normalizeApiBase;

  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;

  Events.emit('runtime:ready', { config: Config });
})();
